export interface ChatbotEmbedConfig {
  layout: {
    iframe: { widthPx: number; heightPx: number };
    floating: { edgeOffsetPx: number; stackGapPx: number };
    motion: {
      panelEnterSec: number;
      messageStaggerSec: number;
      messageFadeSec: number;
      typingDotPulseSec: number;
      sidebarWidthPx: number;
    };
    chatPanel: {
      widthPx: number;
      widthCompactPx: number;
      heightPx: number;
      fullScreen: boolean;
      overlayZIndex: number;
    };
  };
  theme: {
    primary: string;
    text: string;
    onPrimary: string;
    surface: string;
    surfaceMuted: string;
    border: string;
    muted: string;
    headerBorderBottom: string;
  };
  toggle: {
    sizePx: number;
    iconName: string;
    iconCloseName: string;
    iconSize: number;
    backgroundColor: string;
    textColor: string;
    botName: string;
    horizontalPaddingPx: number;
  };
  copy: {
    headerTitle: string;
    headerSubtitle: string;
    greeting: string;
    emptyStateSubtext: string;
    inputPlaceholder: string;
    sendAriaLabel: string;
    newChatLabel: string;
    sessionsHeading: string;
    missingBotIdMessage: string;
  };
  behavior: {
    showGreetingWhenEmpty: boolean;
    showHeaderSubtitle: boolean;
    showHeaderMaximize: boolean;
    suggestionPrompts: string[];
  };
}

export interface ConfigField {
  label: string;
  path: string;
  type: 'text' | 'number' | 'color' | 'toggle' | 'textarea';
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
}

export interface ConfigSection {
  id: string;
  label: string;
  icon: string;
  fields: ConfigField[];
}

export const DEFAULT_CONFIG: ChatbotEmbedConfig = {
  layout: {
    iframe: { widthPx: 960, heightPx: 780 },
    floating: { edgeOffsetPx: 16, stackGapPx: 12 },
    motion: {
      panelEnterSec: 0.18,
      messageStaggerSec: 0.03,
      messageFadeSec: 0.2,
      typingDotPulseSec: 0.45,
      sidebarWidthPx: 280,
    },
    chatPanel: {
      widthPx: 700,
      widthCompactPx: 420,
      heightPx: 600,
      fullScreen: false,
      overlayZIndex: 10000,
    },
  },
  theme: {
    primary: '#1581e5',
    text: '#0f172a',
    onPrimary: '#ffffff',
    surface: '#ffffff',
    surfaceMuted: '#f8fafc',
    border: '#e2e8f0',
    muted: '#64748b',
    headerBorderBottom: '#ffffff33',
  },
  toggle: {
    sizePx: 48,
    iconName: 'bot',
    iconCloseName: 'x',
    iconSize: 24,
    backgroundColor: '#1581e5',
    textColor: '#ffffff',
    botName: '',
    horizontalPaddingPx: 12,
  },
  copy: {
    headerTitle: '',
    headerSubtitle: 'Online',
    greeting: 'How can I help you today?',
    emptyStateSubtext: "Start a conversation and I'll assist you with anything you need.",
    inputPlaceholder: 'Type a message…',
    sendAriaLabel: 'Send message',
    newChatLabel: 'New chat',
    sessionsHeading: 'Chat History',
    missingBotIdMessage: 'Something went wrong, unable to connect to the chatbot. Please try again later.',
  },
  behavior: {
    showGreetingWhenEmpty: true,
    showHeaderSubtitle: true,
    showHeaderMaximize: true,
    suggestionPrompts: [],
  },
};

export const CONFIG_SECTIONS: ConfigSection[] = [
  {
    id: 'layout',
    label: 'Layout Settings',
    icon: 'monitor',
    fields: [
      // { label: 'Iframe Width (px)', path: 'layout.iframe.widthPx', type: 'number' },
      // { label: 'Iframe Height (px)', path: 'layout.iframe.heightPx', type: 'number' },
      // { label: 'Floating Offset (px)', path: 'layout.floating.edgeOffsetPx', type: 'number' },
      // { label: 'Stack Gap (px)', path: 'layout.floating.stackGapPx', type: 'number' },
      { label: 'Panel Width (px)', path: 'layout.chatPanel.widthPx', type: 'number', min: 500, max: 1000 },
      { label: 'Panel Height (px)', path: 'layout.chatPanel.heightPx', type: 'number', min: 400, max: 800 },
      { label: 'Panel Width (Compact)', path: 'layout.chatPanel.widthCompactPx', type: 'number', min: 320, max: 600 },
    ],
  },
  {
    id: 'theme',
    label: 'Color Palette',
    icon: 'sparkles',
    fields: [
      { label: 'Primary', path: 'theme.primary', type: 'color' },
      { label: 'Primary Contrast', path: 'theme.onPrimary', type: 'color' },
      { label: 'Text', path: 'theme.text', type: 'color' },
      { label: 'Surface', path: 'theme.surface', type: 'color' },
      { label: 'Subtle Surface', path: 'theme.surfaceMuted', type: 'color' },
      { label: 'Muted', path: 'theme.muted', type: 'color' },
      { label: 'Component Borders', path: 'theme.border', type: 'color' },
      { label: 'Header Separator', path: 'theme.headerBorderBottom', type: 'color' },
    ],
  },
  {
    id: 'toggle',
    label: 'Toggle & Branding',
    icon: 'bot',
    fields: [
      { label: 'Bot Name', path: 'toggle.botName', type: 'text', placeholder: 'Type a bot name...', maxLength: 12 },
      { label: 'Toggle Size (px)', path: 'toggle.sizePx', type: 'number', min: 40, max: 96 },
      { label: 'Toggle Background', path: 'toggle.backgroundColor', type: 'color' },
      { label: 'Toggle Icon/Text Color', path: 'toggle.textColor', type: 'color' },
      { label: 'Open Icon Name', path: 'toggle.iconName', type: 'text', placeholder: 'e.g. bot' },
      { label: 'Close Icon Name', path: 'toggle.iconCloseName', type: 'text', placeholder: 'e.g. x' },
      { label: 'Icon Size (px)', path: 'toggle.iconSize', type: 'number', min: 16, max: 64 },
      { label: 'Horizontal Padding (px)', path: 'toggle.horizontalPaddingPx', type: 'number', min: 0, max: 40 },
    ],
  },
  {
    id: 'copy',
    label: 'Text Context',
    icon: 'fileText',
    fields: [
      { label: 'Header Title', path: 'copy.headerTitle', type: 'text', placeholder: 'e.g. Chat with AI' },
      { label: 'Header Subtitle', path: 'copy.headerSubtitle', type: 'text', placeholder: 'Online' },
      { label: 'Greeting Message', path: 'copy.greeting', type: 'textarea' },
      { label: 'Empty State Subtext', path: 'copy.emptyStateSubtext', type: 'textarea' },
      { label: 'Input Placeholder', path: 'copy.inputPlaceholder', type: 'text' },
      // { label: 'Send Aria Label', path: 'copy.sendAriaLabel', type: 'text' },
      // { label: 'New Chat Label', path: 'copy.newChatLabel', type: 'text' },
      { label: 'History Heading', path: 'copy.sessionsHeading', type: 'text' },
      { label: 'Error Message (Missing Bot ID)', path: 'copy.missingBotIdMessage', type: 'textarea' },
    ],
  },
  {
    id: 'behavior',
    label: 'Behavior',
    icon: 'settings',
    fields: [
      { label: 'Show Greeting', path: 'behavior.showGreetingWhenEmpty', type: 'toggle' },
      { label: 'Show Subtitle', path: 'behavior.showHeaderSubtitle', type: 'toggle' },
      { label: 'Show Maximize', path: 'behavior.showHeaderMaximize', type: 'toggle' },
    ],
  },
];
